import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Plus, Trash2, Pencil, Check, X, Send, Clock, Receipt, Layers } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/pages/Portal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

const CLIENT_COLOR = { pending: "bg-amber-100 text-amber-700", accepted: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700", expired: "bg-slate-200 text-slate-600" };
let _ridCounter = 0;
const rid = () => `c${Date.now().toString(36)}${_ridCounter++}`;
const newItem = () => ({ item_id: rid(), name: "", unit: "Nos.", hsn_code: "", qty: 1, rate: 0, discount_pct: 0 });
const emptyForm = () => ({
  customer_mode: "walkin", customer_ref_id: "", customer_name: "", customer_address: "", customer_gstin: "", customer_phone: "", customer_email: "",
  place_of_supply: "Tripura", scope_of_work: "", gst_mode: "exclusive", tax_rate: 18, validity_days: 15, payment_terms: "", terms_text: "",
});
const emptySections = () => [{ _key: rid(), name: "Items", items: [newItem()] }];

function previewTotals(sections, gstMode, taxRate) {
  const gross = sections.reduce((s, sec) => s + sec.items.reduce((a, i) => a + (parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0) * (1 - (parseFloat(i.discount_pct) || 0) / 100), 0), 0);
  const taxable = gstMode === "inclusive" ? gross / (1 + taxRate / 100) : gross;
  const tax = taxable * taxRate / 100;
  const grand = gstMode === "inclusive" ? gross : taxable + tax;
  return { taxable: Math.round(taxable * 100) / 100, tax: Math.round(tax * 100) / 100, grand: Math.round(grand) };
}

function SectionsEditor({ sections, setSections }) {
  const upd = (si, ii, field, val) => setSections(sections.map((s, i) => i !== si ? s : { ...s, items: s.items.map((it, j) => j !== ii ? it : { ...it, [field]: val }) }));
  const addItem = (si) => setSections(sections.map((s, i) => i !== si ? s : { ...s, items: [...s.items, newItem()] }));
  const removeItem = (si, ii) => setSections(sections.map((s, i) => i !== si ? s : { ...s, items: s.items.filter((_, j) => j !== ii) }));
  const addSection = () => setSections([...sections, { _key: rid(), name: "", items: [newItem()] }]);
  const removeSection = (si) => setSections(sections.filter((_, i) => i !== si));
  const renameSection = (si, name) => setSections(sections.map((s, i) => i !== si ? s : { ...s, name }));

  return (
    <div className="space-y-4">
      {sections.map((sec, si) => (
        <div key={sec._key || si} className="border border-border rounded-sm p-3 bg-muted/30" data-testid={`quote-section-${si}`}>
          <div className="flex items-center gap-2 mb-2">
            <Layers size={14} className="text-primary shrink-0" />
            <Input placeholder="Section name (e.g. Fire Alarm)" value={sec.name} onChange={(e) => renameSection(si, e.target.value)} className="rounded-sm h-8 text-sm font-semibold" data-testid={`quote-section-name-${si}`} />
            {sections.length > 1 && <Button variant="ghost" size="icon" onClick={() => removeSection(si)} className="shrink-0 text-red-600" data-testid={`quote-remove-section-${si}`}><Trash2 size={14} /></Button>}
          </div>
          <div className="space-y-1.5">
            {sec.items.map((it, ii) => (
              <div key={it.item_id || ii} className="flex flex-wrap gap-1.5 items-center">
                <Input placeholder="Item name" value={it.name} onChange={(e) => upd(si, ii, "name", e.target.value)} className="rounded-sm h-8 flex-1 min-w-[130px] text-xs" data-testid={`quote-item-name-${si}-${ii}`} />
                <Input placeholder="HSN" value={it.hsn_code} onChange={(e) => upd(si, ii, "hsn_code", e.target.value)} className="rounded-sm h-8 w-20 text-xs" data-testid={`quote-item-hsn-${si}-${ii}`} />
                <Input placeholder="Unit" value={it.unit} onChange={(e) => upd(si, ii, "unit", e.target.value)} className="rounded-sm h-8 w-16 text-xs" data-testid={`quote-item-unit-${si}-${ii}`} />
                <Input placeholder="Qty" type="number" value={it.qty} onChange={(e) => upd(si, ii, "qty", e.target.value)} className="rounded-sm h-8 w-16 text-xs" data-testid={`quote-item-qty-${si}-${ii}`} />
                <Input placeholder="Rate" type="number" value={it.rate} onChange={(e) => upd(si, ii, "rate", e.target.value)} className="rounded-sm h-8 w-20 text-xs" data-testid={`quote-item-rate-${si}-${ii}`} />
                <Input placeholder="Disc %" type="number" value={it.discount_pct} onChange={(e) => upd(si, ii, "discount_pct", e.target.value)} className="rounded-sm h-8 w-16 text-xs" data-testid={`quote-item-disc-${si}-${ii}`} />
                <Button variant="ghost" size="icon" onClick={() => removeItem(si, ii)} className="h-8 w-8 shrink-0 text-muted-foreground" data-testid={`quote-remove-item-${si}-${ii}`}><Trash2 size={13} /></Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => addItem(si)} className="rounded-sm mt-2 h-7 text-xs" data-testid={`quote-add-item-${si}`}>+ Add Item</Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addSection} className="rounded-sm gap-1.5" data-testid="quote-add-section-btn"><Plus size={14} /> Add Section</Button>
    </div>
  );
}

export default function Quotations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin";
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [sections, setSections] = useState(emptySections());
  const [saving, setSaving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(() => { api.get("/quotations").then((r) => setList(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) api.get("/users?role=client").then((r) => setClients(r.data)).catch(() => {}); }, [isAdmin]);

  const openNew = async () => {
    setEditId(null);
    let defaults = {};
    try {
      const r = await api.get("/company-settings");
      defaults = { terms_text: r.data.default_terms, validity_days: r.data.default_validity_days, payment_terms: r.data.default_payment_terms };
    } catch (e) { console.error("Could not load company settings defaults", e); toast.error("Could not load company defaults — using blank terms"); }
    setForm({ ...emptyForm(), ...defaults });
    setSections(emptySections());
    setOpen(true);
  };

  const openEdit = (q) => {
    setEditId(q.id);
    setForm({
      customer_mode: q.customer_type === "user" ? "client" : "walkin", customer_ref_id: q.customer_type === "user" ? q.customer_ref_id : "",
      customer_name: q.customer_name || "", customer_address: q.customer_address || "", customer_gstin: q.customer_gstin || "",
      customer_phone: q.customer_phone || "", customer_email: q.customer_email || "", place_of_supply: q.place_of_supply || "Tripura",
      scope_of_work: q.scope_of_work || "", gst_mode: q.gst_mode || "exclusive", tax_rate: q.tax_rate || 18,
      validity_days: q.validity_days || 15, payment_terms: q.payment_terms || "", terms_text: q.terms_text || "",
    });
    setSections(q.sections && q.sections.length ? q.sections : emptySections());
    setOpen(true);
  };

  const patchForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const pickClient = (id) => {
    const c = clients.find((x) => x.id === id);
    patchForm({ customer_ref_id: id, customer_name: c ? (c.company || c.name) : form.customer_name, customer_email: c?.email || "", customer_phone: c?.phone || "" });
  };

  const buildPayload = (status) => ({
    customer_type: form.customer_mode === "client" ? "user" : "customer",
    customer_ref_id: form.customer_mode === "client" ? form.customer_ref_id : "",
    customer_name: form.customer_name, customer_address: form.customer_address, customer_gstin: form.customer_gstin,
    customer_phone: form.customer_phone, customer_email: form.customer_email, place_of_supply: form.place_of_supply,
    sections: sections.filter((s) => s.items.some((i) => i.name)).map((s) => ({ ...s, items: s.items.filter((i) => i.name).map((i) => ({ ...i, qty: parseFloat(i.qty) || 0, rate: parseFloat(i.rate) || 0, discount_pct: parseFloat(i.discount_pct) || 0 })) })),
    scope_of_work: form.scope_of_work, gst_mode: form.gst_mode, tax_rate: parseFloat(form.tax_rate) || 18,
    validity_days: parseInt(form.validity_days) || 15, payment_terms: form.payment_terms, terms_text: form.terms_text, status,
  });

  const save = async (status) => {
    if (!form.customer_name) return toast.error("Enter a customer/party name");
    if (form.customer_mode === "client" && !form.customer_ref_id) return toast.error("Select a client account, or switch to walk-in customer");
    const payload = buildPayload(status);
    if (!payload.sections.length) return toast.error("Add at least one section with a priced item");
    setSaving(true);
    try {
      if (editId) await api.put(`/quotations/${editId}`, payload);
      else await api.post("/quotations", payload);
      toast.success(status === "sent" ? "Quotation sent to customer" : "Draft saved");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const send = async (id) => {
    try { await api.patch(`/quotations/${id}/send`); toast.success("Quotation sent"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  const download = async (id) => {
    try {
      const res = await api.get(`/quotations/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error("Could not open PDF"); }
  };

  const openHistory = async (q) => {
    setHistoryTarget(q);
    try { const r = await api.get(`/quotations/group/${q.quote_group_id}/history`); setHistoryRows(r.data); } catch (e) { setHistoryRows([]); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try { await api.delete(`/quotations/${deleteTarget.id}`); toast.success("Draft deleted"); setDeleteTarget(null); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  const respond = async (id, action, reason = "") => {
    const fd = new FormData(); fd.append("action", action); if (reason) fd.append("reason", reason);
    try { await api.patch(`/quotations/${id}/respond`, fd); toast.success(action === "accept" ? "Quotation accepted" : "Quotation rejected"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  const confirmReject = async () => { await respond(rejectTarget.id, "reject", rejectReason.trim()); setRejectTarget(null); setRejectReason(""); };

  const goCreateBill = (q) => {
    sessionStorage.setItem("sf_billing_prefill", q.quote_group_id);
    navigate("/portal/billing");
  };

  const totals = previewTotals(sections, form.gst_mode, parseFloat(form.tax_rate) || 18);

  return (
    <div className="p-8">
      <PageHeader title="Quotations" subtitle="Sections · GST Modes · Revisions">
        {isAdmin && <Button onClick={openNew} className="rounded-sm bg-primary hover:bg-primary/90 gap-2" data-testid="new-quotation-btn"><Plus size={16} /> New Quotation</Button>}
      </PageHeader>

      <div className="bg-card border border-border rounded-sm overflow-hidden overflow-x-auto">
        <Table className="min-w-[900px]">
          <TableHeader><TableRow className="bg-muted/50">
            <TableHead>Number</TableHead>{isAdmin && <TableHead>Customer</TableHead>}
            <TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead>Client Response</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.map((q) => (
              <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                <TableCell className="font-mono text-xs">{q.quotation_number}{q.revision_number ? `-R${q.revision_number}` : ""}</TableCell>
                {isAdmin && <TableCell>{q.customer_name}</TableCell>}
                <TableCell className="font-semibold">₹{(q.grand_total || 0).toLocaleString("en-IN")}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-sm capitalize">{q.status}</Badge></TableCell>
                <TableCell><Badge className={`rounded-sm capitalize ${CLIENT_COLOR[q.client_status] || ""}`} data-testid={`quote-client-status-${q.id}`}>{q.client_status}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1 flex-wrap">
                    {!isAdmin && q.client_status === "pending" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => respond(q.id, "accept")} className="rounded-sm gap-1 border-green-600 text-green-700 hover:bg-green-50" data-testid={`quote-accept-${q.id}`}><Check size={14} /> Accept</Button>
                        <Button size="sm" variant="outline" onClick={() => { setRejectTarget(q); setRejectReason(""); }} className="rounded-sm gap-1 border-red-600 text-red-700 hover:bg-red-50" data-testid={`quote-reject-${q.id}`}><X size={14} /> Reject</Button>
                      </>
                    )}
                    {isAdmin && q.status === "draft" && <Button size="sm" variant="outline" onClick={() => send(q.id)} className="rounded-sm gap-1" data-testid={`quote-send-${q.id}`}><Send size={14} /> Send</Button>}
                    {isAdmin && q.status === "sent" && <Button size="sm" variant="outline" onClick={() => goCreateBill(q)} className="rounded-sm gap-1" data-testid={`quote-create-bill-${q.id}`}><Receipt size={14} /> Create Bill</Button>}
                    <Button size="sm" variant="outline" onClick={() => openHistory(q)} className="rounded-sm" data-testid={`quote-history-${q.id}`}><Clock size={14} /></Button>
                    {isAdmin && <Button size="sm" variant="outline" onClick={() => openEdit(q)} className="rounded-sm gap-1" data-testid={`quote-edit-${q.id}`}><Pencil size={14} /></Button>}
                    <Button size="sm" variant="outline" onClick={() => download(q.id)} className="rounded-sm gap-1" data-testid={`quote-download-${q.id}`}><Download size={14} /></Button>
                    {isAdmin && q.status === "draft" && <Button size="sm" variant="outline" onClick={() => setDeleteTarget(q)} className="rounded-sm text-red-600 border-red-200" data-testid={`quote-delete-${q.id}`}><Trash2 size={14} /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No quotations yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Edit Quotation" : "Create Quotation"}</DialogTitle>
            <DialogDescription>Editing a quotation that has already been sent creates a new revision automatically — the original stays intact.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={form.customer_mode === "walkin" ? "default" : "outline"} onClick={() => patchForm({ customer_mode: "walkin" })} className="rounded-sm" data-testid="customer-mode-walkin">Walk-in Customer</Button>
              <Button type="button" size="sm" variant={form.customer_mode === "client" ? "default" : "outline"} onClick={() => patchForm({ customer_mode: "client" })} className="rounded-sm" data-testid="customer-mode-client">Client Portal Account</Button>
            </div>
            {form.customer_mode === "client" && (
              <Select value={form.customer_ref_id || "none"} onValueChange={(v) => pickClient(v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-sm" data-testid="quote-client-select"><SelectValue placeholder="Select a client…" /></SelectTrigger>
                <SelectContent><SelectItem value="none">— Select —</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>)}</SelectContent>
              </Select>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase">Party / Customer Name</Label><Input value={form.customer_name} onChange={(e) => patchForm({ customer_name: e.target.value })} className="rounded-sm mt-1" data-testid="quote-customer-name" /></div>
              <div><Label className="text-xs uppercase">GSTIN (optional)</Label><Input value={form.customer_gstin} onChange={(e) => patchForm({ customer_gstin: e.target.value })} className="rounded-sm mt-1" data-testid="quote-customer-gstin" /></div>
              <div><Label className="text-xs uppercase">Phone</Label><Input value={form.customer_phone} onChange={(e) => patchForm({ customer_phone: e.target.value })} className="rounded-sm mt-1" data-testid="quote-customer-phone" /></div>
              <div><Label className="text-xs uppercase">Email</Label><Input value={form.customer_email} onChange={(e) => patchForm({ customer_email: e.target.value })} className="rounded-sm mt-1" data-testid="quote-customer-email" /></div>
              <div className="sm:col-span-2"><Label className="text-xs uppercase">Address</Label><Input value={form.customer_address} onChange={(e) => patchForm({ customer_address: e.target.value })} className="rounded-sm mt-1" data-testid="quote-customer-address" /></div>
              <div><Label className="text-xs uppercase">Place of Supply (state)</Label><Input value={form.place_of_supply} onChange={(e) => patchForm({ place_of_supply: e.target.value })} className="rounded-sm mt-1" data-testid="quote-place-of-supply" /></div>
            </div>

            <div>
              <Label className="text-xs uppercase mb-2 block">Sections &amp; Items</Label>
              <SectionsEditor sections={sections} setSections={setSections} />
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase">GST Mode</Label>
                <Select value={form.gst_mode} onValueChange={(v) => patchForm({ gst_mode: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="quote-gst-mode"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="exclusive">Exclusive (add GST on top)</SelectItem><SelectItem value="inclusive">Inclusive (rates include GST)</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase">Tax Rate %</Label><Input type="number" value={form.tax_rate} onChange={(e) => patchForm({ tax_rate: e.target.value })} className="rounded-sm mt-1" data-testid="quote-tax-rate" /></div>
              <div><Label className="text-xs uppercase">Validity (days)</Label><Input type="number" value={form.validity_days} onChange={(e) => patchForm({ validity_days: e.target.value })} className="rounded-sm mt-1" data-testid="quote-validity" /></div>
            </div>
            <div><Label className="text-xs uppercase">Scope of Work</Label><Textarea rows={3} value={form.scope_of_work} onChange={(e) => patchForm({ scope_of_work: e.target.value })} className="rounded-sm mt-1" data-testid="quote-scope" /></div>
            <div><Label className="text-xs uppercase">Payment Terms</Label><Textarea rows={2} value={form.payment_terms} onChange={(e) => patchForm({ payment_terms: e.target.value })} className="rounded-sm mt-1" data-testid="quote-payment-terms" /></div>
            <div><Label className="text-xs uppercase">Terms &amp; Conditions</Label><Textarea rows={3} value={form.terms_text} onChange={(e) => patchForm({ terms_text: e.target.value })} className="rounded-sm mt-1" data-testid="quote-terms" /></div>

            <div className="bg-muted/50 rounded-sm p-3 text-sm space-y-1" data-testid="quote-preview">
              <div className="flex justify-between text-muted-foreground"><span>Taxable Value</span><span>₹{totals.taxable.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>GST ({form.tax_rate}%)</span><span>₹{totals.tax.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between font-heading font-bold text-secondary pt-1 border-t border-border"><span>Grand Total</span><span>₹{totals.grand.toLocaleString("en-IN")}</span></div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => save("draft")} disabled={saving} className="rounded-sm" data-testid="save-draft-btn">Save as Draft</Button>
            <Button onClick={() => save("sent")} disabled={saving} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="save-send-btn">{editId ? "Save & Send" : "Send to Customer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyTarget} onOpenChange={(o) => !o && setHistoryTarget(null)}>
        <DialogContent data-testid="quote-history-dialog">
          <DialogHeader><DialogTitle className="font-heading">Revision History</DialogTitle><DialogDescription>{historyTarget?.quotation_number}</DialogDescription></DialogHeader>
          <div className="space-y-2 text-sm">
            {historyRows.map((r) => (
              <div key={r.id} className="flex items-center justify-between border-b border-dashed border-border pb-2">
                <div>
                  <p className="font-medium flex items-center gap-1">{r.revision_number ? `Revision R${r.revision_number}` : "Original"} {r.is_latest && <Badge className="rounded-sm bg-primary/10 text-primary">Latest</Badge>}</p>
                  <p className="text-xs text-muted-foreground">{(r.created_at || "").slice(0, 10)} · ₹{(r.grand_total || 0).toLocaleString("en-IN")}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => download(r.id)} data-testid={`history-download-${r.id}`}><Download size={14} /></Button>
              </div>
            ))}
            {historyRows.length === 0 && <p className="text-muted-foreground text-xs">No history found.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-quote-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete this draft?</AlertDialogTitle><AlertDialogDescription>Only drafts can be deleted. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-quote">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-quote">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent data-testid="quote-reject-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reject Quotation?</DialogTitle><DialogDescription>Let us know why (optional).</DialogDescription></DialogHeader>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="rounded-sm" data-testid="quote-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={confirmReject} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="confirm-reject-quote">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
